import { ChatClient, clearChatCache, forgetChatCache } from './chat-client'

/**
 * Pool of ChatClients (PLAN-082): a WebSocket upgrade costs ~0.5–1 s on the
 * edge, so a thread's socket is kept open for a while after its page
 * unmounts and can be opened early from a sidebar hover.
 */

const MAX_IDLE = 3
const IDLE_TTL_MS = 5 * 60_000

interface Entry {
  client: ChatClient
  refs: number
  idleTimer: ReturnType<typeof setTimeout> | null
  lastUsed: number
}

const pool = new Map<string, Entry>()

function key(projectId: string, threadId: string) {
  return `${projectId}:${threadId}`
}

function evictIdle() {
  const idle = [...pool.entries()].filter(([, e]) => e.refs === 0)
  if (idle.length <= MAX_IDLE) return
  idle.sort(([, a], [, b]) => a.lastUsed - b.lastUsed)
  for (const [k] of idle.slice(0, idle.length - MAX_IDLE)) destroy(k)
}

function destroy(k: string) {
  const entry = pool.get(k)
  if (!entry) return
  if (entry.idleTimer) clearTimeout(entry.idleTimer)
  entry.client.close()
  pool.delete(k)
}

export function acquireChat(projectId: string, threadId: string): ChatClient {
  const k = key(projectId, threadId)
  let entry = pool.get(k)
  if (!entry) {
    const client = new ChatClient(projectId, threadId)
    entry = { client, refs: 0, idleTimer: null, lastUsed: Date.now() }
    pool.set(k, entry)
    void client.connect()
  }
  if (entry.idleTimer) {
    clearTimeout(entry.idleTimer)
    entry.idleTimer = null
  }
  entry.refs++
  entry.lastUsed = Date.now()
  return entry.client
}

export function releaseChat(projectId: string, threadId: string): void {
  const k = key(projectId, threadId)
  const entry = pool.get(k)
  if (!entry) return
  entry.refs = Math.max(0, entry.refs - 1)
  entry.lastUsed = Date.now()
  if (entry.refs > 0) return
  entry.client.setTyping(false)
  entry.idleTimer = setTimeout(() => destroy(k), IDLE_TTL_MS)
  evictIdle()
}

/** Sidebar hover: open the socket before the click. No-op if pooled. */
export function warmChat(projectId: string, threadId: string): void {
  if (pool.has(key(projectId, threadId))) return
  acquireChat(projectId, threadId)
  releaseChat(projectId, threadId)
}

/** Thread deleted: close the socket and drop the local copy. */
export async function forgetChat(projectId: string, threadId: string): Promise<void> {
  destroy(key(projectId, threadId))
  await forgetChatCache(projectId, threadId)
}

/** Logout: close everything and wipe every cached thread. */
export async function clearChatPool(): Promise<void> {
  for (const k of [...pool.keys()]) destroy(k)
  await clearChatCache()
}
